import { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { Controller, useWatch } from 'react-hook-form';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import {
  addDays, addYears, isBefore, isValid,
} from 'date-fns';

// Utils
import { cn, formatDate } from 'utils';

// Styles
import styles from './ContactPhoneForm.module.css';

// Components
import Checkbox from '../../components/atoms/Checkbox/Checkbox';
import TextField from '../../components/atoms/TextField/TextField';
import { Picto } from '../../components/atoms/Picto/Picto';
import CalendarInput from '../../components/molecules/CalendarInput/CalendarInput';

// Hooks
import useBookingDates from '../../src/hooks/useBookingDates';

// Services
import { useHourCallBackRanges } from '../../src/services/contact';

const InputPhoneNumber = dynamic(() => import('../../components/atoms/InputPhoneNumber/InputPhoneNumber'), {
  ssr: false,
});

function ContactPhoneForm({
  register, control, errors, setValue, children,
}) {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const calendarRef = useRef();
  const { isDayDisabled } = useBookingDates();

  const dateCallBack = useWatch({ control, name: 'dateCallBack' });

  const { data } = useHourCallBackRanges();

  /**
   * Check if the hour is available for the date selected
   * @param {string} crmValue
   * @param {Date} date
   * @returns {boolean}
   */
  const isAvailable = (crmValue, date) => {
    if (!isValid(date)) return false;
    const now = new Date();
    const [startHour] = crmValue.split('TO').map(Number);
    const startTime = new Date(date.setHours(startHour, 0, 0, 0));

    return isBefore(now, startTime);
  };

  const minDateAvailable = useMemo(() => {
    const now = new Date();
    const lastCrmCode = data?.[data.length - 1]?.code;
    if (!data?.length && !lastCrmCode) return now;
    const [startHour] = lastCrmCode.split('TO').map(Number);
    const startTime = new Date(new Date().setHours(startHour, 0, 0, 0));

    return isBefore(now, startTime) ? now : addDays(now, 1);
  }, [data]);

  return (
    <div className={styles.container}>

      <div className={styles.form}>
        <div className={styles.fieldGroup}>
          <TextField
            label={t('contact.form.firstName')}
            error={errors?.firstName?.message && t(errors?.firstName?.message)}
            {...register('firstName')}
          />
          <TextField
            label={t('contact.form.lastName')}
            error={errors?.lastName?.message && t(errors?.lastName?.message)}
            {...register('lastName')}
          />
        </div>
        <div className={styles.fieldGroup}>
          <div className={styles.field}>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <InputPhoneNumber
                  label={t('contact.form.phone.label')}
                  placeholder={t('contact.form.phone.placeholder')}
                  error={errors?.phone ? t(errors?.phone?.message) : null}
                  {...field}
                />
              )}
            />
          </div>
          <TextField
            type="email"
            error={errors?.email?.message && t(errors?.email?.message)}
            label={t('contact.form.email')}
            {...register('email')}
          />
        </div>

        <div className={styles.fieldGroup}>
          <Controller
            control={control}
            name="dateCallBack"
            render={({ field }) => (
              <div className={styles.calendar}>
                <div className={styles.fieldGroupTitle}>{t('contact.form.picker.dateCallBack')}</div>
                <div
                  className={cn([styles.frame])}
                >
                  <Picto icon="calendarMonth" className="margin-right-24 margin-right-16-mobile" />
                  <span
                    className="p1-desktop-bold font-color-blue margin-right-24 margin-right-16-mobile p1-mobile-bold"
                  >
                    {field?.value ? formatDate(field?.value) : t('contact.form.picker.select')}
                  </span>
                </div>
                <div className={styles.calendarInput}>
                  <CalendarInput
                    ref={calendarRef}
                    minDate={minDateAvailable}
                    maxDate={addYears(new Date(), 1)}
                    handleChange={(value) => {
                      setValue('hourCallBackRangeCode', '');
                      field?.onChange(value);
                    }}
                    tileDisabled={(date) => (isDayDisabled({ ...date }))}
                    startDate={field?.value}
                  />
                </div>
                {errors?.dateCallBack?.message && (
                  <span className={styles.error}>
                    {t(errors?.dateCallBack?.message)}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        <div className={styles.fieldGroup}>
          <Controller
            control={control}
            name="hourCallBackRangeCode"
            render={({ field }) => (
              <div className={styles.hoursContainer}>
                <div className={styles.fieldGroupTitle}>{t('contact.form.picker.hourCallBack')}</div>
                <div className={styles.hours}>
                  {data?.length ? data?.map(({ code, labels }) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => field?.onChange(code)}
                      className={cn([styles.hourButton, field?.value === code ? styles.active : ''])}
                      disabled={!isValid(dateCallBack) || !isAvailable(code, dateCallBack)}
                    >
                      {labels?.[locale]}
                    </button>
                  )) : null}
                </div>
                {errors?.hourCallBackRangeCode?.message && (
                  <span className={styles.error}>
                    {t(errors?.hourCallBackRangeCode?.message)}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        <div className={styles.fieldGroup}>
          <Checkbox
            id="newsletter"
            error={errors?.newsletter?.message && t(errors?.newsletter?.message)}
            label={t('contact.form.newsletter')}
            theme="light"
            {...register('newsletter')}
          />
        </div>
        {children}
      </div>
    </div>
  );
}

ContactPhoneForm.propTypes = {
  register: PropTypes.func.isRequired,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({
    firstName: PropTypes.shape({ message: PropTypes.string }),
    lastName: PropTypes.shape({ message: PropTypes.string }),
    phone: PropTypes.shape({ message: PropTypes.string }),
    email: PropTypes.shape({ message: PropTypes.string }),
    dateCallBack: PropTypes.shape({ message: PropTypes.string }),
    hourCallBackRangeCode: PropTypes.shape({ message: PropTypes.string }),
    newsletter: PropTypes.shape({ message: PropTypes.string }),
  }),
  setValue: PropTypes.func,
  children: PropTypes.node,
};

ContactPhoneForm.defaultProps = {
  errors: {},
  setValue: () => {},
  children: null,
};

export default ContactPhoneForm;
